import React, { useState, useEffect, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { ThemeContext } from '../context/ThemeContext';
import { API_BASE } from '../api';

function OperatorTasks() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentTheme } = useContext(ThemeContext);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [operatorName, setOperatorName] = useState('');


  const colors = {
    backgroundDark: '#0f0c29', 
    backgroundMedium: '#302b63', 
    backgroundLight: '#24243e',
    textLight: 'rgba(255, 255, 255, 0.8)',
    borderDark: 'rgba(255, 255, 255, 0.2)',
    primary: currentTheme?.primary || '#6366F1',
    secondary: currentTheme?.secondary || '#8B5CF6',
    accent: currentTheme?.accent || '#06B6D4',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
  };

  const videoTypeConfig = {
    'indoor_reel': { label: '🏠 Indoor Reel', color: '#6366F1' },
    'outdoor_reel': { label: '🌳 Outdoor Reel', color: '#10B981' },
    'podcast': { label: '🎙️ Podcast', color: '#F59E0B' },
    'indoor_long_video': { label: '🏠 Indoor Long', color: '#8B5CF6' },
    'outdoor_long_video': { label: '🌳 Outdoor Long', color: '#06B6D4' }
  };

  const styles = {
    container: {
      minHeight: '100vh',
      background: `linear-gradient(135deg, ${colors.backgroundDark} 0%, ${colors.backgroundMedium} 50%, ${colors.backgroundLight} 100%)`,
      position: 'relative',
      overflowX: 'hidden'
    },
    animatedBackground: { position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' },
    floatingOrb: { position: 'absolute', borderRadius: '50%', filter: 'blur(40px)', opacity: 0.3, animation: 'float 8s ease-in-out infinite' },
    orb1: { width: '300px', height: '300px', background: colors.primary, top: '10%', left: '5%', animationDelay: '0s' },
    orb2: { width: '200px', height: '200px', background: colors.secondary, top: '60%', right: '10%', animationDelay: '2s' },
    orb3: { width: '250px', height: '250px', background: colors.accent, bottom: '20%', left: '15%', animationDelay: '4s' },
    orb4: { width: '180px', height: '180px', background: colors.success, top: '30%', right: '20%', animationDelay: '6s' },
    gridOverlay: {
      position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
      backgroundImage: `linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)`,
      backgroundSize: '50px 50px'
    },
    header: { padding: '30px 30px 20px', position: 'relative', zIndex: 2 },
    headerContent: { maxWidth: '1200px', margin: '0 auto' },
    greeting: { display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '12px' },
    pageTitle: { fontSize: '2.2rem', fontWeight: '800', background: 'linear-gradient(135deg, #fff 0%, #a5b4fc 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', margin: 0 },
    welcomeBadge: { background: 'rgba(255, 255, 255, 0.1)', padding: '6px 12px', borderRadius: '16px', border: `1px solid ${colors.borderDark}`, backdropFilter: 'blur(10px)', display: 'flex', alignItems: 'center', gap: '6px' },
    badgeText: { color: 'white', fontWeight: '600', fontSize: '0.8rem' },
    badgeRole: { background: colors.primary, color: 'white', padding: '2px 6px', borderRadius: '10px', fontSize: '0.65rem', fontWeight: '600' },
    pageSubtitle: { color: colors.textLight, fontSize: '1rem', margin: 0 },
    content: { padding: '0 30px 30px', maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 2 },
    formCard: { background: 'rgba(255, 255, 255, 0.05)', borderRadius: '20px', border: `1px solid ${colors.borderDark}`, backdropFilter: 'blur(10px)', overflow: 'hidden' },
    cardHeader: { background: `linear-gradient(135deg, ${colors.primary}, ${colors.secondary})`, color: 'white', padding: '20px 25px' },
    cardTitle: { fontSize: '1.5rem', fontWeight: '700', margin: 0 },
    backBtn: { background: 'rgba(255, 255, 255, 0.1)', color: 'white', border: `1px solid ${colors.borderDark}`, padding: '10px 18px', borderRadius: '10px', fontWeight: '600', fontSize: '0.9rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', textDecoration: 'none', marginBottom: '15px' },
    loadingState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 30px', color: 'white', textAlign: 'center' },
    spinner: { width: '18px', height: '18px', border: '2px solid transparent', borderTop: '2px solid white', borderRadius: '50%', animation: 'spin 1s linear infinite' },
    tableHeader: { background: 'rgba(255, 255, 255, 0.1)', color: 'white', borderBottom: `2px solid ${colors.borderDark}`, padding: '10px 8px', fontWeight: '600', fontSize: '0.8rem', whiteSpace: 'nowrap' },
    tableRow: { borderBottom: '1px solid rgba(255, 255, 255, 0.05)', color: 'white', fontSize: '0.8rem' },
    tableCell: { padding: '8px', verticalAlign: 'middle', fontSize: '0.8rem' },
    actionBtn: { background: 'rgba(255, 255, 255, 0.1)', color: 'white', border: `1px solid ${colors.borderDark}`, padding: '8px 15px', borderRadius: '10px', fontWeight: '600', fontSize: '0.8rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' },
    smallBadge: { padding: '3px 8px', borderRadius: '8px', fontSize: '0.7rem', fontWeight: '600' },
    linkButton: { background: 'rgba(255, 255, 255, 0.1)', color: 'white', border: `1px solid ${colors.borderDark}`, padding: '4px 8px', borderRadius: '6px', cursor: 'pointer', fontSize: '0.7rem' },
    videoTypeBadge: { background: 'rgba(139, 92, 246, 0.2)', color: '#8B5CF6', padding: '3px 6px', borderRadius: '6px', fontSize: '0.7rem', fontWeight: '500' }
  };

  useEffect(() => {
    const operator = location.state?.operatorName || 'Unknown Operator';
    setOperatorName(operator);
    fetchOperatorTasks(operator);
  }, [location.state]);

  const fetchOperatorTasks = async (operator) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/get_operator_tasks.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ operator_name: operator })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        setTasks(data.tasks || []);
      }
    } catch (error) {
      setMessage('❌ Error loading tasks: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoNameClick = (task) => navigate('/task-full-details', { state: { task } });
  const handleClientClick = (clientName) => navigate('/client-tasks', { state: { clientName, filter: 'client' } });
  const handleEditorClick = (editorName, editorId = null) => {
    if (!editorName || editorName === 'Unassigned') return;
    const editorTasks = tasks.filter(task => getEditorDisplayName(task) === editorName);
    navigate('/editor-tasks', { state: { editorId, editorName, tasks: editorTasks } });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { class: 'bg-warning text-dark', text: 'Pending', icon: 'fa-clock' },
      assigned: { class: 'bg-primary text-white', text: 'Assigned', icon: 'fa-user-check' },
      in_progress: { class: 'bg-info text-white', text: 'In Progress', icon: 'fa-spinner' },
      completed: { class: 'bg-success text-white', text: 'Completed', icon: 'fa-check' },
      approved: { class: 'bg-success text-white', text: 'Approved', icon: 'fa-check-circle' },
      unassigned: { class: 'bg-secondary text-white', text: 'Unassigned', icon: 'fa-user-times' }
    };
    const config = statusConfig[status] || statusConfig.pending;
    return <span className={`badge ${config.class}`} style={styles.smallBadge}><i className={`fas ${config.icon} me-1`}></i>{config.text}</span>;
  };

  const getVideoTypeBadge = (videoType) => {
    if (!videoType) return <span className="badge" style={{...styles.videoTypeBadge, background: 'rgba(255,255,255,0.1)', color: 'white'}}>-</span>;
    const config = videoTypeConfig[videoType] || { label: videoType, color: '#6B7280' };
    return <span className="badge" style={{...styles.videoTypeBadge, background: `${config.color}20`, color: config.color}}>{config.label}</span>;
  };

  const getEditorDisplayName = (task) => task.editor_name || task.editor || 'Unassigned';
  const isValidLink = (link) => link && typeof link === 'string' && !['-', 'NULL', 'null', ''].includes(link.trim());
  const truncateText = (text, maxLength = 12) => (!text ? '-' : text.length <= maxLength ? text : text.substring(0, maxLength) + '...');

  if (loading) return <Layout><div style={styles.container}><div style={styles.loadingState}><div style={styles.spinner}></div><h3>Loading Operator Tasks</h3></div></div></Layout>;

  return (
    <Layout>
      <div style={styles.container}>
        <div className="animated-background" style={styles.animatedBackground}>
          <div className="floating-orb orb-1" style={{...styles.floatingOrb, ...styles.orb1}}></div>
          <div className="floating-orb orb-2" style={{...styles.floatingOrb, ...styles.orb2}}></div>
          <div className="floating-orb orb-3" style={{...styles.floatingOrb, ...styles.orb3}}></div>
          <div className="floating-orb orb-4" style={{...styles.floatingOrb, ...styles.orb4}}></div>
          <div className="grid-overlay" style={styles.gridOverlay}></div>
        </div>

        <div style={styles.header}>
          <div style={styles.headerContent}>
            <div style={styles.greeting}><h1 style={styles.pageTitle}>Operator Tasks</h1></div>
            <p style={styles.pageSubtitle}>All tasks handled by {operatorName}</p>
          </div>
        </div>

        <div style={styles.content}>
          <div style={{display: 'flex', gap: '12px', marginBottom: '15px'}}><button style={styles.backBtn} onClick={() => navigate(-1)}>Back</button></div>

          <div className="tasks-card" style={styles.formCard}>
            <div style={styles.cardHeader}><h3 style={styles.cardTitle}>{operatorName} - Tasks List</h3></div>
            <div style={{overflowX: 'auto'}}>
              <table className="table table-hover mb-0" style={{width: '100%', borderCollapse: 'collapse'}}>
                <thead>
                  <tr>
                    <th style={styles.tableHeader}>#</th>
                    <th style={styles.tableHeader}>Video</th>
                    <th style={styles.tableHeader}>Type</th>
                    <th style={styles.tableHeader}>Client</th>
                    <th style={styles.tableHeader}>Status</th>
                    <th style={styles.tableHeader}>Editor</th>
                    <th style={styles.tableHeader}>#</th>
                    <th style={styles.tableHeader}>Raw</th>
                    <th style={styles.tableHeader}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((task, index) => {
                    const editorName = getEditorDisplayName(task);
                    return (
                      <tr key={task.id} style={styles.tableRow}>
                        {/* 1. ID COLUMN (#) - Changed to White */}
                        <td style={{...styles.tableCell, fontWeight: 'bold', color: '#FFFFFF'}}>
                          {index + 1}
                        </td>
                        
                        {/* 2. VIDEO COLUMN - Changed to White, Icon Removed */}
                        <td style={styles.tableCell}>
                          <button 
                            className="btn btn-link p-0 text-decoration-none border-0 bg-transparent"
                            onClick={() => handleVideoNameClick(task)}
                            style={{ color: '#FFFFFF', fontWeight: '600', textAlign: 'left', fontSize: '0.8rem' }}
                          >
                            {truncateText(task.video_name, 10)}
                          </button>
                        </td>

                        <td style={styles.tableCell}>{getVideoTypeBadge(task.video_type)}</td>
                        
                        {/* 3. CLIENT COLUMN - Changed to White, Icon Removed */}
                        <td style={styles.tableCell}>
                          <button 
                            className="btn btn-link p-0 text-decoration-none border-0 bg-transparent"
                            onClick={() => handleClientClick(task.client_name)}
                            style={{ color: '#FFFFFF', fontWeight: '600', textAlign: 'left', fontSize: '0.8rem' }}
                          >
                            {truncateText(task.client_name, 8)}
                          </button>
                        </td>
                        
                        <td style={styles.tableCell}>{getStatusBadge(task.status)}</td>
                        
                        {/* 4. EDITOR COLUMN - Changed to White, Icon Removed */}
                        <td style={styles.tableCell}>
                          {editorName !== 'Unassigned' ? (
                            <button 
                              className="btn btn-link p-0 text-decoration-none border-0 bg-transparent"
                              onClick={() => handleEditorClick(editorName)}
                              style={{ color: '#FFFFFF', fontWeight: '600', textAlign: 'left', fontSize: '0.8rem' }}
                            >
                              {truncateText(editorName, 8)}
                            </button>
                          ) : (
                            <span style={{color: 'rgba(255,255,255,0.5)'}}>Unassigned</span>
                          )}
                        </td>
                        
                        <td style={styles.tableCell}><span className="badge" style={{background: colors.accent, color: 'white', ...styles.smallBadge}}>{task.video_count || '0'}</span></td>
                        
                        <td style={styles.tableCell}>
                          <button style={{...styles.linkButton, opacity: isValidLink(task.raw_link) ? 1 : 0.4}} onClick={() => isValidLink(task.raw_link) && window.open(task.raw_link, '_blank')}>
                            <i className="fas fa-external-link-alt"></i>
                          </button>
                        </td>
                        
                        <td style={styles.tableCell}>
                          <small style={{ color: colors.textLight, fontSize: '0.75rem' }}>
                            {new Date(task.shoot_date).toLocaleDateString('en-US', {month: 'short', day: 'numeric'})}
                          </small>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <style jsx>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        @keyframes float { 0%, 100% { transform: translate(0px, 0px) scale(1); } 33% { transform: translate(30px, -50px) scale(1.1); } 66% { transform: translate(-20px, 20px) scale(0.9); } }
        .table-hover tbody tr:hover { background: rgba(255, 255, 255, 0.05) !important; }
      `}</style>
    </Layout>
  );
}

export default OperatorTasks;